import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  Mail, 
  ArrowRight, 
  ArrowLeft,
  Lightbulb,
  Zap,
  Rocket,
  Check,
  Inbox,
  Settings2
} from "lucide-react";
import { useAuth, API } from "../App";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    automation_level: "suggestions",
    tone_preference: "formal",
    email_connected: false
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const automationLevels = [
    { 
      id: "suggestions", 
      name: "Suggestions uniquement", 
      icon: Lightbulb, 
      description: "L'IA propose, vous décidez. Contrôle total.",
      color: "bg-green-500/10 text-green-500"
    },
    { 
      id: "semi_automatic", 
      name: "Semi-automatique", 
      icon: Zap, 
      description: "Actions automatiques avec validation requise.",
      color: "bg-blue-500/10 text-blue-500"
    },
    { 
      id: "automatic", 
      name: "Automatique", 
      icon: Rocket, 
      description: "L'IA agit selon vos règles prédéfinies.",
      color: "bg-purple-500/10 text-purple-500"
    }
  ];

  const toneOptions = [
    { id: "formal", name: "Formel", description: "Vouvoiement, ton professionnel strict" },
    { id: "commercial", name: "Commercial", description: "Engageant, orienté client et solution" },
    { id: "friendly", name: "Cordial", description: "Chaleureux mais professionnel" }
  ];

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/onboarding/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la configuration");
      }

      const result = await response.json();
      setUser({ ...user, onboarding_completed: true, ...data });
      toast.success(`Configuration terminée ! ${result.emails_generated} emails démo générés.`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                Bienvenue sur ALIA !
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Configurons votre assistant email en quelques étapes.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Inbox className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 dark:text-white mb-1">
                    Ce qu'ALIA va faire pour vous
                  </h3>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                    <li>• Catégoriser automatiquement vos emails</li>
                    <li>• Générer des réponses intelligentes</li>
                    <li>• Détecter les relances à effectuer</li>
                    <li>• Convertir les emails en tâches</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                Connexion boîte mail
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Pour le MVP, nous utilisons des emails de démonstration.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">Mode Démonstration</h4>
                  <p className="text-sm text-slate-500">Emails simulés pour tester la plateforme</p>
                </div>
                <Check className="w-6 h-6 text-green-500 ml-auto" />
              </div>
              
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Des emails réalistes seront générés pour vous permettre de tester toutes les fonctionnalités.
                L'intégration Gmail/Outlook sera disponible prochainement.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                Niveau d'automatisation
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Choisissez le niveau de contrôle que vous souhaitez garder.
              </p>
            </div>

            <RadioGroup
              value={data.automation_level}
              onValueChange={(value) => setData({ ...data, automation_level: value })}
              className="space-y-3"
            >
              {automationLevels.map((level) => (
                <div key={level.id}>
                  <RadioGroupItem
                    value={level.id}
                    id={level.id}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={level.id}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      data.automation_level === level.id
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-blue-500/50'
                    }`}
                    data-testid={`automation-${level.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${level.color}`}>
                      <level.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 dark:text-white">{level.name}</span>
                        {data.automation_level === level.id && (
                          <Check className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{level.description}</p>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                Préférences de ton
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Comment souhaitez-vous que l'IA rédige vos réponses ?
              </p>
            </div>

            <RadioGroup
              value={data.tone_preference}
              onValueChange={(value) => setData({ ...data, tone_preference: value })}
              className="space-y-3"
            >
              {toneOptions.map((tone) => (
                <div key={tone.id}>
                  <RadioGroupItem
                    value={tone.id}
                    id={tone.id}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={tone.id}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      data.tone_preference === tone.id
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-blue-500/50'
                    }`}
                    data-testid={`tone-${tone.id}`}
                  >
                    <div>
                      <span className="font-medium text-slate-900 dark:text-white">{tone.name}</span>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{tone.description}</p>
                    </div>
                    {data.tone_preference === tone.id && (
                      <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Summary */}
            <div className="mt-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Récapitulatif
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Check className="w-4 h-4 text-green-500" />
                  Automatisation: {automationLevels.find(l => l.id === data.automation_level)?.name}
                </li>
                <li className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Check className="w-4 h-4 text-green-500" />
                  Ton: {toneOptions.find(t => t.id === data.tone_preference)?.name}
                </li>
                <li className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Check className="w-4 h-4 text-green-500" />
                  Mode: Démonstration (emails simulés)
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
        </div>
        <span className="text-sm text-slate-500">
          Étape {step} sur {totalSteps}
        </span>
      </div>

      {/* Progress */}
      <div className="px-4">
        <Progress value={progress} className="h-1" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-xl glass-card" data-testid="onboarding-card">
          <CardContent className="p-6 md:p-8">
            {renderStep()}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  data-testid="onboarding-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              ) : (
                <div></div>
              )}

              {step < totalSteps ? (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleNext}
                  data-testid="onboarding-next-btn"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleComplete}
                  disabled={loading}
                  data-testid="onboarding-complete-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Configuration...
                    </div>
                  ) : (
                    <>
                      Lancer ALIA
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
